import { z } from "zod";
import {
  DOB_BANK_LABEL,
  PAYMONGO_CHANNEL_LABEL,
  PAYMONGO_CHANNELS,
} from "#/lib/payment-types";

export const shippingAddressSchema = z.object({
  first_name: z.string().min(1, "Required"),
  last_name: z.string().min(1, "Required"),
  email: z.string().email("Enter a valid email"),
  phone: z.string().min(7, "Enter a phone number"),
  address_1: z.string().min(1, "Required"),
  address_2: z.string(),
  barangay: z.string(),
  city: z.string().min(1, "Enter a city"),
  province: z.string().min(1, "Enter a state / region"),
  postal_code: z.string().min(3, "Required"),
  country_code: z
    .string()
    .length(2, "Use ISO-2 country code (e.g. ph)")
    .transform((v) => v.toLowerCase()),
});

export const cardDetailsSchema = z.object({
  account_number: z.string().min(16, "Must be 16 digits").max(16),
  ccv: z.string().min(3, "Min 3 digits").max(4),
  expires_at: z.string().regex(/^(0[1-9]|1[0-2])\/\d{2}$/, "Use MM/YY"),
});

export type ShippingAddressForm = z.infer<typeof shippingAddressSchema>;

export const EMPTY_SHIPPING_ADDRESS: ShippingAddressForm = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  address_1: "",
  address_2: "",
  barangay: "",
  city: "",
  province: "",
  postal_code: "",
  country_code: "ph",
};

export type PaymongoChannel = (typeof PAYMONGO_CHANNELS)[number];

export const DRAGONPAY_CHANNELS = ["any", "metrobank", "bdo"] as const;

export const paymentMethodSchema = z
  .discriminatedUnion("kind", [
    z.object({
      kind: z.literal("paymongo"),
      channel: z.enum(PAYMONGO_CHANNELS),
      cardDetails: cardDetailsSchema.optional(),
      bankCode: z.string().optional(),
    }),
    z.object({
      kind: z.literal("dragonpay"),
      channel: z.enum(DRAGONPAY_CHANNELS),
    }),
  ])
  .superRefine((v, ctx) => {
    if (v.kind !== "paymongo") return;
    if (v.channel === "card" && !v.cardDetails) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["cardDetails"],
        message: "Card details are required",
      });
    }
    if (v.channel === "dob" && !v.bankCode) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        path: ["bankCode"],
        message: "Select a bank",
      });
    }
  });

export type PaymentMethodDraft = z.infer<typeof paymentMethodSchema>;

export const DRAGONPAY_CHANNEL_LABEL: Record<
  (typeof DRAGONPAY_CHANNELS)[number],
  string
> = {
  any: "Choose on Dragonpay",
  bdo: "BDO",
  metrobank: "Metrobank",
};

export const MANUAL_CHANNEL_LABEL: Record<"cod" | "bank_transfer", string> = {
  cod: "Cash on Delivery",
  bank_transfer: "Bank Transfer",
};

// Spread onto cart/order metadata as flat, primitive keys so the chosen method
// is readable in the Medusa admin. Its metadata editor renders any nested
// object/array as a disabled "complex" row that hides the value, so every key
// here must stay a top-level string. `payment_method` is the human headline
// shown to admins; `payment_kind`/`payment_channel` drive the order-status
// reader (PaymentInstructions).
export type PaymentMethodMetadata = {
  payment_method: string;
  payment_kind: PaymentMethodDraft["kind"];
  payment_channel: string;
};

export function buildPaymentMethodMetadata(
  pm: PaymentMethodDraft,
): PaymentMethodMetadata {
  if (pm.kind === "paymongo") {
    const bankLabel =
      pm.channel === "dob" && pm.bankCode
        ? (DOB_BANK_LABEL[pm.bankCode] ?? pm.bankCode)
        : null;
    return {
      payment_kind: pm.kind,
      payment_channel: pm.channel,
      payment_method: bankLabel ?? PAYMONGO_CHANNEL_LABEL[pm.channel],
    };
  }
  return {
    payment_kind: pm.kind,
    payment_channel: pm.channel,
    payment_method: DRAGONPAY_CHANNEL_LABEL[pm.channel],
  };
}

const promoCodeSchema = z.object({
  code: z.string().optional(),
  isValid: z.boolean()
})

const EMPTY_PROMO_CODE = {
  code: "",
  isValid: false
}

const pickupSelectionSchema = z.object({
  hub: z.string(),
  pickupDate: z.string(),
  timeSlotId: z.string(),
})

export type PickupSelection = z.infer<typeof pickupSelectionSchema>

const pickupDetailsSchema = pickupSelectionSchema.extend({
  held: pickupSelectionSchema.nullable(),
})

export type PickupDetailsType = z.infer<typeof pickupDetailsSchema>

export const EMPTY_PICKUP_DETAILS: PickupDetailsType = {
  hub: "",
  pickupDate: "",
  timeSlotId: "",
  held: null,
}

export const checkoutDraftSchema = z.object({
  address: shippingAddressSchema,
  shipping_option_id: z.string().min(1, "Choose a shipping option"),
  payment_method: paymentMethodSchema,
  promo_code: promoCodeSchema,
  pickupDetails: pickupDetailsSchema
});

export type CheckoutDraft = z.infer<typeof checkoutDraftSchema>;

export const EMPTY_CHECKOUT_DRAFT: CheckoutDraft = {
  address: EMPTY_SHIPPING_ADDRESS,
  shipping_option_id: "",
  payment_method: { kind: "paymongo", channel: "" },
  promo_code: EMPTY_PROMO_CODE,
  pickupDetails: EMPTY_PICKUP_DETAILS
};

export const checkoutSearchSchema = z.object({});

export type CheckoutSearch = z.infer<typeof checkoutSearchSchema>;
